import { PayloadAction } from '@reduxjs/toolkit';
import { AuthSelectors, ConfigSelectors, StatsActions, StatsSelectors } from '@web_opxp/features';
import { LanguageSelectors, LanguageService } from '@web_opxp/language';
import { all, call, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import { TApiFetchResponse } from 'web_core_library';
import * as UnityEngine from 'web_unity_engine';
import { GAMEPLAY_BACKGROUND, GAMEPLAY_FULLSCREEN, GAMEPLAY_SOUND } from '../base/constants';
import * as ENV from '../base/env';
import { FULLSCREEN_CLASS } from '../base/fullscreenHelper';
import { isFetchErrorCanBeSkipped } from '../base/utils';
import ConfigService from './configService';
import * as Firebase from './firebaseActions';
import { getCurrentExerciseName, isUnityStarted, isWorkoutRunning } from './selectors';
import { UnityActions } from './slice';
import { IInitUnityPayload, IStatState, ITutorialState, UnityError } from './types';
import {
  parseReadStateStat,
  parseSolutionConfirmStat,
  parseTutorialStateStat,
  replaceCategoryGroupIdPlaceholder,
  testForCategoryGroupIdPlaceholder,
} from './utils';

export function* initUnityEngineSaga(action: PayloadAction<IInitUnityPayload>) {
  const { preloading, basePath } = action.payload;
  // check if already started
  const isStarted = yield select(isUnityStarted);
  if (isStarted) {
    return;
  }
  yield put(Firebase.unityEngineEventAction('', 'onboarding', Firebase.EEngineTrackingEvent.startLoading, preloading));
  // preload translations to show translated errors
  yield call(loadUnityTranslationsSaga);
  const unityPath = basePath === undefined || basePath === '' ? ENV.UnityPath : basePath;
  // initialize config service with base path
  yield call(ConfigService.setBasePath, unityPath);
  // step 1 - load appConfig
  const appConfig = yield call(loadUnityAppConfigSaga);
  appConfig.basePath = unityPath;
  const isModerator: boolean = yield select(AuthSelectors.isModerator);
  const enableLogging = isModerator || ENV.UnityDebug ? 1 : 0;
  appConfig.enableLogging = enableLogging;
  const language = yield select(LanguageSelectors.getLanguage);
  appConfig.language = language;
  // apply general localization
  const t = LanguageService.getTFunction();
  appConfig.localization = t('unity.general:unity', {
    returnObjects: true,
  });
  // step 2 - start engine
  const canvasId = process.env.NX_PUBLIC_UNITY_ID ?? 'exercise';
  // filenames will be hardcoded for now, we can implement env vars later
  const dataUrl = 'Production.data.unityweb';
  const codeUrl = 'Production.wasm.unityweb';
  const frameworkUrl = 'Production.framework.js.unityweb';
  yield put(
    UnityEngine.Actions.unityInit(appConfig.basePath, canvasId, FULLSCREEN_CLASS, dataUrl, codeUrl, frameworkUrl)
  );
  yield take(UnityEngine.Actions.unityAppReady);
  yield put(UnityEngine.Actions.unityAppInit(appConfig));
  yield take(UnityEngine.Actions.unityEngineReady);
  yield put(Firebase.unityEngineEventAction('', 'onboarding', Firebase.EEngineTrackingEvent.finishLoading, preloading));
  yield put(UnityActions.unityStarted());
}

export function* loadUnityTranslationsSaga() {
  try {
    const language = yield select(LanguageSelectors.getLanguage);
    const errorlocaleLoaded = LanguageService.hasResourceBundle(language, 'common');
    const localeLoaded = LanguageService.hasResourceBundle(language, 'unity.general');
    if (!localeLoaded || !errorlocaleLoaded) {
      // load unity general namespace and common namespace with error locales
      yield call(LanguageService.loadNamespaces, ['unity.general', 'common']);
    }
  } catch (error) {
    if (isFetchErrorCanBeSkipped(error)) {
      // ignore request aborted errors
      return;
    }
    throw new UnityError('Localization loading failed');
  }
}

export function* loadUnityAppConfigSaga() {
  try {
    const appConfigResponse: TApiFetchResponse<typeof ConfigService.getUnityAppConfig> = yield call(
      ConfigService.getUnityAppConfig
    );
    return appConfigResponse.data;
  } catch (error) {
    if (isFetchErrorCanBeSkipped(error)) {
      // ignore request aborted errors
      return;
    }
    const t = LanguageService.getTFunction();
    throw new UnityError(t('opxp.common:Errors.Unity.LoadUnityAppConfigFailed'));
  }
}

export function* prepareExerciseSaga(action: PayloadAction<string>) {
  const exerciseId = action.payload;
  yield put(Firebase.unityEngineEventAction(exerciseId, 'onboarding', Firebase.EEngineTrackingEvent.startLoadingEx));
  // fetch workout config from S3
  const workoutSettings = yield call(loadExerciseSettings, exerciseId);
  // get translations
  const exerciseLocale = yield call(loadExerciseTranslations, exerciseId);
  // overload existing localization with loaded one
  const locale = {
    ...workoutSettings.localization,
    ...exerciseLocale,
  };
  workoutSettings.localization = locale;
  // get user settings
  const soundStat = yield select(StatsSelectors.getStatIntValueById, GAMEPLAY_SOUND);
  const backgroundStat = yield select(StatsSelectors.getStatIntValueById, GAMEPLAY_BACKGROUND);
  const userId = yield select(AuthSelectors.getUserId);
  // apply user settings
  const gameplaySettings: UnityEngine.IGameplaySettings = {
    sound: soundStat !== null ? soundStat : 1,
    background: backgroundStat !== null ? backgroundStat : 1,
    uid: userId,
    balancingName: 'onboarding',
  };
  workoutSettings.userSettings = gameplaySettings;
  yield put(UnityEngine.Actions.unityExerciseInit(workoutSettings));
  yield take(UnityEngine.Actions.unityExerciseReady);
  yield put(Firebase.unityEngineEventAction(exerciseId, 'onboarding', Firebase.EEngineTrackingEvent.finishLoadingEx));
  yield put(UnityActions.exerciseReady());
}

export function* loadExerciseSettings(exerciseId: string) {
  try {
    const workoutSettingsResponse: TApiFetchResponse<typeof ConfigService.getWorkoutSettings> = yield call(
      ConfigService.getWorkoutSettings,
      exerciseId
    );
    return workoutSettingsResponse.data;
  } catch (error) {
    if (isFetchErrorCanBeSkipped(error)) {
      // ignore request aborted errors
      return;
    }
    const t = LanguageService.getTFunction();
    throw new UnityError(t('opxp.common:Errors.Unity.LoadWorkoutSettingsFailed'));
  }
}

export function* loadExerciseTranslations(exerciseId: string) {
  const t = LanguageService.getTFunction();
  try {
    const language = yield select(LanguageSelectors.getLanguage);
    const exerciseNamespace = `unity.${exerciseId}`;
    const localeLoaded = LanguageService.hasResourceBundle(language, exerciseNamespace);
    if (!localeLoaded) {
      // load general namespace
      yield call(LanguageService.loadNamespaces, exerciseNamespace);
    }
    const exerciseLocale = LanguageService.getResourceBundle(language, exerciseNamespace);
    return exerciseLocale;
  } catch (error) {
    if (isFetchErrorCanBeSkipped(error)) {
      // ignore request aborted errors
      return;
    }
    throw new UnityError(t('opxp.common:Errors.Unity.LoadExerciseTranslationsFailed'));
  }
}

export function* getUserSettingsFromStats(categoryGroupId?: number) {
  if (!categoryGroupId) {
    return {};
  }
  const tutorialStatId = `0-${categoryGroupId}-9`;
  const tutorialStatValue: string | undefined = yield select(StatsSelectors.getStatStringValueById, tutorialStatId);
  const tutorialStatLastEdit: number | undefined = yield select(StatsSelectors.getStatLastEdit, tutorialStatId);
  const tutorialState: ITutorialState | undefined = yield call(
    parseTutorialStateStat,
    tutorialStatValue,
    tutorialStatLastEdit
  );
  const readStateStatId = `0-${categoryGroupId}-10`;
  const readStateStatValue: string | undefined = yield select(StatsSelectors.getStatStringValueById, readStateStatId);
  const readState: IStatState | undefined = yield call(parseReadStateStat, readStateStatValue);
  const solutionConfirmStatId = `0-${categoryGroupId}-11`;
  const solutionConfirmStatValue: string | undefined = yield select(
    StatsSelectors.getStatStringValueById,
    solutionConfirmStatId
  );
  const solutionConfirm: IStatState | undefined = yield call(parseSolutionConfirmStat, solutionConfirmStatValue);
  return {
    tutorial: tutorialState,
    messagesReadState: readState,
    showSolutionWithConfirmState: solutionConfirm,
  };
}

export function* startExerciseSaga() {
  const exerciseId = yield select(getCurrentExerciseName);
  yield put(Firebase.unityEngineEventAction(exerciseId, 'onboarding', Firebase.EEngineTrackingEvent.start));
  const categoryGroupId: number | undefined = yield select(ConfigSelectors.getCategoryGroupId, exerciseId);
  const userSettings = yield call(getUserSettingsFromStats, categoryGroupId);
  const options: UnityEngine.ExerciseWorkoutOptions = {
    difficulty: 0,
    mode: UnityEngine.WorkoutMode.Workout,
    options: JSON.stringify({
      preventInputPause: true,
      timeout: 60,
    }),
    ...userSettings,
  };
  yield put(UnityEngine.Actions.unityExerciseStart(options));
  // wait for result
  const unityCompleteAction = yield take(UnityEngine.Actions.unityExerciseComplete);
  const unityWorkoutResult: UnityEngine.WorkoutUnityResult = JSON.parse(unityCompleteAction.payload);
  yield put(UnityActions.exerciseComplete(unityWorkoutResult));
  yield put(StatsActions.saveStatsQueue());
  // this should be taken care by unity engine library now
  // yield call(toggleFullscreenOff);
  yield put(Firebase.unityEngineEventAction(exerciseId, 'onboarding', Firebase.EEngineTrackingEvent.complete));
}

export function* trackEventSaga({ payload }: ReturnType<typeof UnityEngine.Actions.unityTrackEvent>) {
  const exerciseName = yield select(getCurrentExerciseName);
  try {
    if (payload) {
      const eventsObject = JSON.parse(payload);
      for (const event in eventsObject) {
        if (Object.hasOwn(eventsObject, event)) {
          yield put(Firebase.firebaseUnityTrackingAction(exerciseName, event, eventsObject[event]));
        }
      }
    }
  } catch {
    yield call(console.log, 'tracking event format incorrect:', payload);
  }
}

export function* updateUnityGameplaySoundSettingsSaga({
  payload,
}: ReturnType<typeof UnityEngine.Actions.unityGameplaySoundSettingsUpdated>) {
  const isRunning = yield select(isWorkoutRunning);
  if (!isRunning) {
    return;
  }
  yield put(StatsActions.updateNumericStat(GAMEPLAY_SOUND, payload));
}

export function* updateUnityGameplayBackgroundSettingsSaga({
  payload,
}: ReturnType<typeof UnityEngine.Actions.unityGameplayBackgroundSettingsUpdated>) {
  const isRunning = yield select(isWorkoutRunning);
  if (!isRunning) {
    return;
  }
  yield put(StatsActions.updateNumericStat(GAMEPLAY_BACKGROUND, payload));
}

export function* updateUnityGameplayFullscreenSettingsSaga({
  payload,
}: ReturnType<typeof UnityEngine.Actions.unityGameplayFullscreenSettingsUpdated>) {
  const isRunning = yield select(isWorkoutRunning);
  if (!isRunning) {
    return;
  }
  yield put(StatsActions.updateNumericStat(GAMEPLAY_FULLSCREEN, payload));
}

export function* updateUserStatsSaga({
  payload: { statId, payload },
}: ReturnType<typeof UnityEngine.Actions.unityUpdateStats>) {
  const isRunning = yield select(isWorkoutRunning);
  if (!isRunning) {
    return;
  }
  try {
    // parse statId for placeholders
    let parsedStatId = statId;
    if (testForCategoryGroupIdPlaceholder(parsedStatId)) {
      const exerciseId = yield select(getCurrentExerciseName);
      const categoryGroupId: number | undefined = yield select(ConfigSelectors.getCategoryGroupId, exerciseId);
      parsedStatId = replaceCategoryGroupIdPlaceholder(parsedStatId, categoryGroupId);
    }
    yield put(StatsActions.updateGenericStat(parsedStatId, payload));
  } catch (error) {
    yield call(console.log, error);
  }
}

export default function* unityWatcher() {
  yield all([
    takeEvery(UnityActions.initUnity, initUnityEngineSaga),
    takeEvery(UnityActions.prepareExercise, prepareExerciseSaga),
    takeEvery(UnityActions.startExercise, startExerciseSaga),
    takeEvery(UnityEngine.Actions.unityTrackEvent, trackEventSaga),
    takeLatest(UnityEngine.Actions.unityGameplaySoundSettingsUpdated, updateUnityGameplaySoundSettingsSaga),
    takeLatest(UnityEngine.Actions.unityGameplayBackgroundSettingsUpdated, updateUnityGameplayBackgroundSettingsSaga),
    takeEvery(UnityEngine.Actions.unityGameplayFullscreenSettingsUpdated, updateUnityGameplayFullscreenSettingsSaga),
    takeEvery(UnityEngine.Actions.unityUpdateStats, updateUserStatsSaga),
  ]);
}
